<template>
  <div
    v-if="canReadDiscussions"
    v-on-clickaway="hideDiscussion"
    class="asset-discussions"
  >
    <div
      v-if="chatWindowOpen"
      class="asset-discussions-wrapper"
    >
      <div class="discussion-header">
        <h3 class="label-heading">
          Asset Discussions
        </h3>
      </div>
      <div
        v-if="discussions"
        class="discussion-body"
      >
        <div
          v-for="discussion in discussions"
          :key="discussion.id"
          class="discussion-content"
        >
          <div class="discussion-row">
            <div
              v-tooltip="showInfo(discussion)"
              class="discussion-author"
            >
              {{ discussionAuthor(discussion.admin_user.first_name, discussion.admin_user.last_name) }}
            </div>
            <p class="discussion-message">
              {{ discussion.message }}
            </p>
          </div>
        </div>
      </div>
      <div class="discussion-footer">
        <textarea
          v-model="discussionMessage"
          @keypress="showUsers($event)"
          @click="chatLive = true"
          ref="dm"
          :class="[{ 'expand-chat': chatLive}, 'chat-area']"
          placeholder="Type something"
        />
        <div
          :class="[{ 'hide-list': !showUsersList }, 'users-list']"
        >
          <hlt-multiselect
            ref="userSelection"
            v-model="selectedUser"
            :options="adminUsers"
            :multiple="false"
            :show-labels="false"
            label="full_name"
            track-by="id"
            class="admin-select"
            placeholder="Select a user"
            v-on-clickaway="() => showUsersList = false"
          />
        </div>
        <button
          class="discussion-chat-button button"
          :disabled="canSaveMessage"
          @click.prevent="sendMessage"
        >
          Send
        </button>
      </div>
    </div>
    <div
      class="discussion-close-circle"
      @click="activateChat"
    >
      <close-svg v-if="chatWindowOpen" />
      <chat-svg v-else />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import railsRoutes from '../../../store/rails-routes'
import date from '../../../mixins/date'
import { mixin as clickaway } from 'vue-clickaway2'
import axios from 'axios'
import HLTMultiselect from 'vue-multiselect'
import CloseSvg from '@/assets/images/icons/close-discussion.svg'
import ChatSvg from '@/assets/images/icons/chat-bubble.svg'

export default {
  components: {
    'hlt-multiselect': HLTMultiselect,
    CloseSvg,
    ChatSvg
  },
  mixins: [ date, clickaway ],
  data () {
    return {
      discussionMessage: '',
      chatWindowOpen: false,
      chatLive: false,
      showUsersList: false,
      adminUsers: [],
      areaFocus: null,
      user: null
    }
  },
  computed: {
    ...mapGetters([
      'currentUser',
      'asset',
      'assetType',
      'topLevelAsset',
      'assignment'
    ]),
    selectedUser: {
      get () {
        return this.user
      },
      set (optionValue) {
        this.user = optionValue
        this.addUser(optionValue)
      }
    },
    discussions () {
      if (this.mainAsset && this.mainAsset.content_discussions) {
        let assets = this.mainAsset.content_discussions
        return assets.sort((a, b) => a.created_at - b.created_at)
      } else {
        return []
      }
    },
    mainAsset () {
      if (this.assetType && this.assetType === 'topic') {
        return this.topLevelAsset
      } else if (this.assignment) {
        return this.assignment
      } else {
        return this.asset
      }
    },
    resourceType () {
      if (this.assignment) {
        return 'AssetAssignment'
      } else if (this.assetType === 'item') {
        return 'V3Flashcard'
      } else {
        let name = this.assetType
        name = name.charAt(0).toUpperCase() + this.assetType.slice(1)
        return name
      }
    },
    canSaveMessage () {
      if (this.canWriteDiscussions) {
        return this.discussionMessage === ''
      } else {
        return false
      }
    },
    canWriteDiscussions () {
      if (this.currentUser) {
        if (this.$router.isRoute('topic')) {
          return this.currentUser.permissions.can_write_topics
        } else if (this.$router.isRoute('attachment')) {
          return this.currentUser.permissions.can_write_attachments
        } else if (this.$router.isRoute('item')) {
          return this.currentUser.permissions.can_write_flashcards
        } else if (this.$router.isRoute('grouping')) {
          return this.currentUser.permissions.can_write_groupings
        } else if (this.$router.isRoute('asset_assignment')) {
          return this.currentUser.permissions.is_a_asset_assigner || this.currentUser.permissions.is_a_copy_writer || this.currentUser.permissions.is_a_copy_editor
        } else {
          return false
        }
      } else {
        return false
      }
    },
    canReadDiscussions () {
      if (this.currentUser) {
        if (this.$router.isRoute('topic')) {
          return this.currentUser.permissions.can_read_topics
        } else if (this.$router.isRoute('attachment')) {
          return this.currentUser.permissions.can_read_attachments
        } else if (this.$router.isRoute('item')) {
          return this.currentUser.permissions.can_read_flashcards
        } else if (this.$router.isRoute('grouping')) {
          return this.currentUser.permissions.can_read_groupings
        } else if (this.$router.isRoute('asset_assignment')) {
          return this.currentUser.permissions.is_a_asset_assigner || this.currentUser.permissions.is_a_copy_writer || this.currentUser.permissions.is_a_copy_editor
        } else {
          return false
        }
      } else {
        return false
      }
    }
  },
  created () {
    this.getUsers()
  },
  updated () {
    if (this.showUsersList) {
      this.$refs.userSelection.$el.focus()
    }
  },
  methods: {
    ...mapActions([
      'create'
    ]),
    showUsers (evt) {
      if (evt.charCode === 64) {
        const area = this.$refs.dm
        this.areaFocus = area.selectionStart
        this.showUsersList = true
      }
    },
    getUsers () {
      const url = railsRoutes.api_path({ type: 'admin_users' })
      axios.get(url).then(response => {
        this.adminUsers = this._.orderBy(response.data.records, ['full_name'], ['asc'])
      })
    },
    addUser (user) {
      let userName = `${user.first_name}_${user.last_name} `
      this.discussionMessage = this.discussionMessage.substring(0, this.areaFocus + 1) +
                               userName +
                               this.discussionMessage.substring(this.areaFocus + 1, this.discussionMessage.length);
      const area = this.$refs.dm
      area.selectionStart = this.areaFocus
      area.focus()
      this.showUsersList = false
    },
    activateChat () {
      this.chatWindowOpen = !this.chatWindowOpen
    },
    hideDiscussion () {
      this.chatWindowOpen = false
      this.chatLive = false
    },
    sendMessage () {
      let url = railsRoutes.api_path({ type: 'content_discussions' })
      let messageData = {
        resource_id: this.mainAsset.id,
        resource_type: this.resourceType,
        message_type: 'comment',
        message: this.discussionMessage
      }
      axios.post(url, messageData)
        .then(response => {
          this.mainAsset.content_discussions.push(response.data.records[0])
          this.discussionMessage = ''
          this.$notify({ title: 'Message Saved', type: 'success' })
        }).catch((err) => {
          this.$notify({ title: 'Error saving message', type: 'error' })
          console.error('Error: ' + err)
        })
    },
    discussionAuthor (fname, lname) {
      if (fname && lname) {
        let fn = fname.charAt(0).toUpperCase()
        let ln = lname.charAt(0).toUpperCase()
        return fn + ln
      } else {
        return 'HL'
      }
    },
    showInfo (info) {
      if (this.discussions) {
        let authDate = this.prettyDate(info.created_at)
        let fName = info.admin_user.first_name
        let lName = info.admin_user.last_name
        if (fName === undefined || fName === null) {
          fName = 'New'
        }
        if (lName === undefined || lName === null) {
          lName = 'User'
        }
        return `
          ${fName} ${lName} <br>
          ${authDate}
        `
      } else {
        return 'Unknown Source'
      }
    }
  }

}
</script>

<style lang="scss">
.asset-discussions {
  position: fixed;
  bottom: 175px;
  right: 50px;
  height: auto;
  box-shadow: 0 9px 14px 2px rgba(0, 0, 0, 0.2);
  z-index: 999;
  .asset-discussions-wrapper {
    width: 400px;
    z-index: 9999;
    display: flex;
    flex: 1 1 auto;
    height: auto;
    max-height: 600px;
    flex-direction: column;
    background: $white-2;
    .discussion-header {
      display: flex;
      flex: 1 1 5%;
      justify-content: center;
      align-items: center;
      background: $blue-2;
      h3 {
        text-align: center;
        margin: 0 auto;
        font-size: 15.8px;
        color: $white-1;
        margin: 0;
        padding: .5rem 0;
      }
    }
    .hide-list {
      display: none;
    }
    .discussion-body {
      display: flex;
      flex: 1 1 85%;
      flex-wrap: wrap;
      padding: .5rem .75rem;
      overflow: auto;
      color: $grey-1;
      font-size: .85rem;
      align-items: flex-start;
      .discussion-time {
        display: flex;
        font-size: .75rem;
        flex: 1;
      }
      .discussion-content {
        min-width: 100%;
        margin: 10px 0;
        .discussion-row {
          display: flex;
          flex-wrap: wrap;
          .discussion-author {
            height: 31.5px;
            width: 31.5px;
            color: $white-2;
            background: $blue-4;
            border-radius: 50%;
            font-size: .875rem;
            margin-right: 10px;
            text-align: center;
            padding: 5px;
            cursor: pointer;
          }
          .discussion-message {
            display: flex;
            color: $blue-2;
            font-size: 15.8px;
            flex: 1 75%;
          }
        }
      }
    }
    .discussion-footer {
      display: flex;
      flex: 1 1 15%;
      .chat-area {
        height: 4rem;
        position: relative;
        margin: 0;
        padding: 10px;
        border: 0;
        border-top: 2px solid $grey-4;
        resize: none;
        transition: all 0.5 ease;
        z-index: 99999;
        &.expand-chat {
          height: 10rem;
          margin-bottom: 60px;
        }
      }
      .users-list {
        position: absolute;
        background: $cloud-white;
        width: 200px;
        border-radius: 4px;
        box-shadow: 0 3px 8px rgba(0,0,0,.25);
        top: 10px;
        left: 20px;
        z-index: 99999;
      }
      .list-user {
        cursor: pointer;
      }
      .discussion-chat-button {
        position: absolute;
        bottom: 15px;
        right: 15px;
        border: 2px solid $grey-2;
        background: $white-2;
        color: $grey-2;
        font-size: 13.5px;
        text-transform: uppercase;
        padding: .5rem .75rem;
      }
    }
  }
  .discussion-close-circle {
    position: fixed;
    bottom: 75px;
    right: 50px;
    margin: 1rem 0;
    height: 47px;
    width: 47px;
    border-radius: 50%;
    cursor: pointer;
    background: $white-2;
    display: flex;
    box-shadow: 2px 9px 8px 2px rgba(0, 0, 0, 0.2);
    justify-content: center;
    align-items: center;
    img {
      width: 25px !important;
    }
    .close-icon {
      align-self: center;
      align-items: center;
      height: 27px;
      width: 27px;
      stroke: $blue-4;
    }
  }
}
</style>
